import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, type Me } from '../api';
import { cents, centsOrNull, money, moneyInput } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

interface Dividend { id: number; decidedOn: string; recipient: string; personalCode: string | null; gross: number; gpmRate: number; gpm: number; net: number; paidOn: string | null; note: string }
interface Draft { id: number | null; decidedOn: string; recipient: string; personalCode: string; gross: string; rate: string; paidOn: string; note: string }

const today = () => new Date().toLocaleDateString('sv-SE');
const empty = (): Draft => ({ id: null, decidedOn: today(), recipient: '', personalCode: '', gross: '', rate: '15', paidOn: '', note: '' });

/**
 * Dividendai / paskirstytas pelnas: the owners' decision to pay out profit, the income tax withheld (15 % by
 * default) and the day it was paid — posted in the journal and declared in GPM313 (boxes 8–10).
 */
export function DividendsPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const readOnly = me.company.role === 'viewer' || Boolean(me.viewAsAdmin);
  const data = useQuery({ queryKey: ['dividends'], queryFn: () => api.get<{ dividends: Dividend[] }>('api/v1/dividends') });
  const [draft, setDraft] = useState<Draft | null>(null);
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: async () => { setDraft(null); await queryClient.invalidateQueries({ queryKey: ['dividends'] }); await queryClient.invalidateQueries({ queryKey: ['journal'] }); },
  });
  const set = (patch: Partial<Draft>) => draft && setDraft({ ...draft, ...patch });
  const save = () => {
    if (!draft) return;
    const body = { decidedOn: draft.decidedOn, recipient: draft.recipient, personalCode: draft.personalCode, gross: cents(draft.gross),
      gpmRate: Math.round(Number(draft.rate.replace(',', '.')) * 100), paidOn: draft.paidOn || null, note: draft.note };
    act.mutate(() => (draft.id ? api.put(`api/v1/dividends/${draft.id}`, body) : api.post('api/v1/dividends', body)));
  };
  const rows = data.data?.dividends ?? [];
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Buhalterija')} title={t('Dividendai')} text={t('Paskirstytas pelnas savininkams: sprendimas, išskaičiuotas GPM ir išmokėjimo diena (žurnale ir GPM313).')} />}>
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}
      <section className="panel-card">
        <div className="card-head"><div><p className="eyebrow">{t('Sprendimai')}</p><h2>{t('Dividendai')}</h2></div>
          {readOnly || draft ? null : <button type="button" className="button button--primary button--small" onClick={() => setDraft(empty())}>+ {t('Naujas sprendimas')}</button>}</div>
        <p className="muted">{t('Banke išmokėtą sumą (be GPM) pažymėkite priežastimi „Dividendai“. GPM sumokamas VMI: išmokėjus iki 15 d. – iki mėnesio pabaigos, vėliau – iki kito mėnesio 15 d.')}</p>
        {draft ? (
          <div className="journal-draft">
            <div className="form-grid form-grid--4">
              <label>{t('Sprendimo data')}<input type="date" value={draft.decidedOn} onChange={(e) => set({ decidedOn: e.target.value })} /></label>
              <label className="span-2">{t('Gavėjas')}<input value={draft.recipient} maxLength={200} onChange={(e) => set({ recipient: e.target.value })} /></label>
              <label>{t('Asmens kodas')}<input value={draft.personalCode} inputMode="numeric" maxLength={11} autoComplete="off" onChange={(e) => set({ personalCode: e.target.value })} /></label>
              <label>{t('Suma prieš GPM')}<input inputMode="decimal" value={draft.gross} onChange={(e) => set({ gross: e.target.value })} /></label>
              <label>{t('GPM tarifas, %')}<input inputMode="decimal" value={draft.rate} onChange={(e) => set({ rate: e.target.value })} /></label>
              <label>{t('Išmokėta')}<input type="date" value={draft.paidOn} min={draft.decidedOn} onChange={(e) => set({ paidOn: e.target.value })} /></label>
              <label>{t('Pastaba')}<input value={draft.note} maxLength={300} onChange={(e) => set({ note: e.target.value })} /></label>
            </div>
            <div className="form-actions">
              <button type="button" className="button button--primary" disabled={act.isPending || !draft.recipient.trim() || !centsOrNull(draft.gross)} onClick={save}>{t('Išsaugoti')}</button>
              <button type="button" className="button button--ghost" onClick={() => setDraft(null)}>{t('Atšaukti')}</button>
            </div>
          </div>
        ) : null}
        {rows.length ? (
          <table className="journal-lines">
            <thead><tr><th>{t('Sprendimo data')}</th><th>{t('Gavėjas')}</th><th className="right">{t('Suma prieš GPM')}</th><th className="right">GPM</th><th className="right">{t('Išmokėti')}</th><th>{t('Išmokėta')}</th><th /></tr></thead>
            <tbody>{rows.map((d) => (
              <tr key={d.id}>
                <td className="journal-date">{d.decidedOn}</td><td>{d.recipient}{d.note ? <small>{d.note}</small> : null}</td>
                <td className="right">{money(d.gross)}</td><td className="right">{money(d.gpm)}</td><td className="right">{money(d.net)}</td>
                <td className="journal-date">{d.paidOn ?? '—'}</td>
                <td className="account-actions">{readOnly ? null : <>
                  <button type="button" className="text-link" onClick={() => setDraft({ id: d.id, decidedOn: d.decidedOn, recipient: d.recipient, personalCode: d.personalCode ?? '', gross: moneyInput(d.gross), rate: String(d.gpmRate / 100).replace('.', ','), paidOn: d.paidOn ?? '', note: d.note })}>{t('Keisti')}</button>
                  <button type="button" className="text-link" onClick={() => { if (window.confirm(t('Ištrinti šį sprendimą?'))) act.mutate(() => api.delete(`api/v1/dividends/${d.id}`)); }}>{t('Ištrinti')}</button>
                </>}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : data.isSuccess && !draft ? <p className="empty">{t('Dividendų sprendimų dar nėra.')}</p> : null}
      </section>
    </PanelShell>
  );
}
